import {Injectable} from '@angular/core';
import {DcpEntryGroup, DcpSubtotalGroups} from '@dcp/configurator';
import {Cart} from '@spartacus/cart/base/root';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {KeyValuePair} from '../../cart/dcp-cart-configuration.model';
import {OrderEntryConfiguration} from '../../model/order/dcp-order-entry.model';
import {EntryGroupService} from '../../product/services/entry-group.service';
import {DcpActiveCartService} from '../../services/dcp-active-cart.service';
import {DcpOrderEntryService} from '../../services/dcp-order-entry.service';
import {CartDataService} from './cart-data.service';

@Injectable({
  providedIn: 'root'
})
export class ContractPriceService {

  constructor(
    private activeCartService: DcpActiveCartService,
    private orderEntryService: DcpOrderEntryService,
    private cartDataService: CartDataService,
    private entryGroupService: EntryGroupService
  ) {
  }

  getGroupTotal(entryGroups: DcpEntryGroup[], cart$?: Observable<Cart>): Observable<string> {
    return this.getContractTotalByCode(entryGroups, 'all', cart$);
  }

  getDerivativeTotal(entryGroups: DcpEntryGroup[]): Observable<string> {
    return this.getContractTotalByCode(entryGroups, 'derivatives');
  }

  /**
   * Get the 'all' or 'recurring' formattedValue for a splitGroup. Looks for the pricing object of
   * priceTotalsBySalesArea by building an identifier based on the vendor, transactionType & duration
   *
   * @param code 'all' for splitGroup.code=='all'.formattedValues.totalValue or
   *             'recurring' for splitGroup.code=='recurring'.formattedValues.netValue
   * @param entry with the displayType PRODUCT
   * @param cart$ Subscription to the current Cart
   */
  getSplitGroupTotal(code: 'all' | 'recurring', entry: OrderEntry, cart$: Observable<Cart>): Observable<any> {
    const configuration: OrderEntryConfiguration = this.orderEntryService.getConfiguration(entry);
    const salesArea: string = this.orderEntryService.getSalesArea(entry)?.salesOrganizationERP;
    const identifier: string =
      `${configuration?.vendorId}` +
      `_${salesArea}` +
      `.${entry?.transactionType}` +
      `.${configuration?.contract ? configuration.contract : 'NA'}` +
      `.${configuration?.endDate}`;
    return cart$.pipe(
      map(cart => {
        const priceTotals: {} = this.cartDataService.getPriceTotalsBySalesArea(cart);
        const splitGroupTotals: any[] = priceTotals?.[identifier] ?? [];
        const formattedValues = splitGroupTotals?.find(total => total?.code === code)?.formattedValues;
        return code === 'all' ? formattedValues?.totalValue : formattedValues?.netValue;
      }));
  }

  /**
   * Gets the Price totals of the first product.
   * They should be identical for all products within the provider group.
   *
   * @param dcpEntryGroups array containing the root bundle of the current entryGroups.
   * @param cart$ subscription to ActiveCart or Active Order.
   */
  private getContractPriceTotals(
    dcpEntryGroups: DcpEntryGroup[],
    cart$: Observable<Cart> = this.activeCartService.getActiveCart()
  ): Observable<DcpSubtotalGroups[]> {
    const entry: OrderEntry = this.entryGroupService.getFirstEntry(dcpEntryGroups);
    const contractId: string = this.orderEntryService.getConfiguration(entry)?.contract;
    return cart$.pipe(
      map(cart => {
          const priceTotalsByContract: KeyValuePair<any> = this.cartDataService.getPriceTotalsByContract(cart);
          if (contractId && priceTotalsByContract) {
            return priceTotalsByContract[contractId];
          }
          return null;
        }
      )
    );
  }

  private getContractTotalByCode(
    entryGroups: DcpEntryGroup[],
    key: string,
    cart$?: Observable<Cart>): Observable<string> {
    const contractPriceTotals$: Observable<DcpSubtotalGroups[]> = this.getContractPriceTotals(entryGroups, cart$);
    return contractPriceTotals$.pipe(map(contractPriceTotals => {
      if (contractPriceTotals) {
        return contractPriceTotals.find(pt => pt.code === key)?.formattedValues.totalValue;
      }
      return '';
    }));
  }
}
